import {KatButton, KatDateRangePicker, KatDropdown, KatSpinner} from "@amzn/katal-react";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {
    CASE_TYPE_TO_CASE_SUB_TYPE_MAP,
    CASE_TYPE_OPTION, NA_COUNTRY_OPTION,
    REGION_COUNTRY_CONFIG, REVIEW_TYPE_OPTION,
    STATUS_OPTION,
    TRANSPORTER_TYPE_OPTION
} from "src/app/config/DropDownConfig";
import {ReviewEntry} from "src/app/model/ReviewEntry";
import {EEPApi} from "src/app/api/EEPApi";
import {CsvUtil} from "src/app/util/CsvUtil";
import {QueryCondition} from "src/app/model/QueryCondition";
import {StringUtil} from "src/app/util/StringUtil";
import {Validator} from "src/app/util/Validator";
import ReviewPreviewList from "src/app/component/export/ReviewPreviewList";
import {ApiUtil} from "src/app/util/ApiUtil";
import {DropDownOption} from "src/app/model/DropDownOption";
import {Region} from "src/app/constant/Enums";
import validateDateRange = Validator.validateDateRange;
import {REVIEW_EXPORT_FILE_HEADER} from "src/app/config/TableConfig";
import userInfoStore from "src/app/store/UserInfoStore";

const DEFAULT_REFERENCE_SUB_TYPE = 'DP';
const DEFAULT_CASE_TYPE = 'Abuse';
const DEFAULT_CASE_SUB_TYPE = 'OPF';
const DEFAULT_REVIEW_STATUS = 'PENDING';
const DEFAULT_REVIEW_TYPE = 'PRIMARY_AUDIT';

const CURRENT_DATE = new Date();
const START = new Date(CURRENT_DATE);
START.setDate(CURRENT_DATE.getDate() - 14);

const END = new Date(CURRENT_DATE);
END.setDate(CURRENT_DATE.getDate() + 1);

const END_DATE = END.toISOString();
const START_DATE = START.toISOString();

const ReviewExport = () => {
    const currentUrl = window.location.href;

    console.log("read user info from local store" + userInfoStore.value);

    const referenceSubType = useRef<any>(null);
    const caseTypeRef = useRef<any>(null);
    const caseSubTypeRef = useRef<any>(null);
    const countryRef = useRef<any>(null);
    const reviewStatusRef = useRef<any>(null);
    const reviewTypeRef = useRef<any>(null);
    const dateRangeRef = useRef<any>(null);

    const [loading, setLoading] = useState(true);
    const [caseSubTypeOptions, setCaseSubTypeOptions] = useState<any>();

    function initQueryCondition() {
        const queryParams = new URLSearchParams(window.location.search);
        const initReferenceSubType = queryParams?.get('referenceSubType') || DEFAULT_REFERENCE_SUB_TYPE;
        const initCaseType = queryParams?.get('caseType') || DEFAULT_CASE_TYPE;
        const initCaseSubType = queryParams?.get('caseSubType') || DEFAULT_CASE_SUB_TYPE;
        const initCountry = queryParams?.get('country') || countryOption[0].value;
        const initReviewStatus = queryParams?.get('reviewStatus') || DEFAULT_REVIEW_STATUS;
        const initReviewType = queryParams?.get('reviewType') || DEFAULT_REVIEW_TYPE;
        const initStartDate = queryParams?.get('startDate') || START_DATE;
        const initEndDate = queryParams?.get('endDate') || END_DATE;
        return {
            referenceSubType: initReferenceSubType,
            caseType: initCaseType,
            caseSubType: initCaseSubType,
            reviewType: initReviewType,
            country: initCountry,
            reviewStatus: initReviewStatus,
            startDate: initStartDate,
            endDate: initEndDate
        }
    }

    const [countryOption, setCountryOption] = useState<DropDownOption[]>(() => {
        if (currentUrl.indexOf(Region.NA) != -1) {
            return REGION_COUNTRY_CONFIG.get(Region.NA) || [];
        }
        if (currentUrl.indexOf(Region.EU) != -1) {
            return REGION_COUNTRY_CONFIG.get(Region.EU) || [];
        }
        if (currentUrl.indexOf(Region.FE) != -1) {
            return REGION_COUNTRY_CONFIG.get(Region.FE) || [];
        } else {
            return NA_COUNTRY_OPTION;
        }
    });

    const initCondition: QueryCondition = initQueryCondition();
    const [queryCondition, setQueryCondition] = useState<QueryCondition>(initCondition);

    const [reviewList, setReviewList] = useState<ReviewEntry[]>([]);
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<any>();

    useEffect(() => {
        (async () => {
            try {
                const response = await EEPApi.listReview(ApiUtil.createListReviewRequest(initCondition, lastEvaluatedKey));
                const list = response.data.list;
                setLastEvaluatedKey(response.data.lastEvaluatedKey);
                setReviewList(list);
                setCaseSubTypeOptions(CASE_TYPE_TO_CASE_SUB_TYPE_MAP[initCondition.caseType] || []);
            } catch (error: any) {
                console.error("Error while loading review list:", error);
                console.log(error.message);
                console.log(error.response?.data?.message);
                if (error.response?.data?.message != undefined) {
                    alert(error.response?.data?.message);
                } else {
                    alert("Error while loading review list," + error.message + " Please try again later!");
                }
            } finally {
                setLoading(false);
            }
        })();

    }, []);

    const downloadAll = async () => {
        try {
            setLoading(true);
            const response = await EEPApi.exportReview(ApiUtil.createExportReviewRequest(initCondition));
            const link = response.data.link;
            const fileName = response.data.fileName;
            await ApiUtil.downloadFile(link, fileName);
        } catch (error: any) {
            console.error("Error while downloading all review list:", error);
            console.log(error.message);
            console.log(error.response?.data?.message);
            if (error.response?.data?.message != undefined) {
                alert(error.response?.data?.message);
            } else {
                alert("Error while downloading all reviews," + error.message + " Please try again later!");
            }
        } finally {
            setLoading(false);
        }
    }

    const downloadPreview = async () => {
        try {
            setLoading(true);
            await CsvUtil.generateCsv(reviewList, REVIEW_EXPORT_FILE_HEADER);
        } catch (error) {
            console.error("Error while downloading preview review list:", error);
            alert("Error while downloading preview review list, please try again later!");
        } finally {
            setLoading(false);
        }
    }

    const conditionChange = () => {
        const start = dateRangeRef.current.__startValue;
        const end = dateRangeRef.current.__endValue;
        const errMsg = validateDateRange(start, end, 90);
        if (errMsg) {
            alert(errMsg);
            return;
        } else {
            // Get the current selected case type
            const selectedCaseType = caseTypeRef.current.__value;
            let newCaseSubType = caseSubTypeRef.current.__value;
            if (initCondition.caseType != selectedCaseType) {
                const newSubTypeOptions = CASE_TYPE_TO_CASE_SUB_TYPE_MAP[selectedCaseType] || [];
                if (newSubTypeOptions.length > 0) {
                    newCaseSubType = newSubTypeOptions[0].value;
                }

                // Update case subtype options based on selected case type
                setCaseSubTypeOptions(newSubTypeOptions);
            }
            const queryCondition = {
                referenceSubType: referenceSubType.current.__value,
                caseType: caseTypeRef.current.__value,
                caseSubType: newCaseSubType,
                country: countryRef.current.__value,
                reviewStatus: reviewStatusRef.current.__value,
                reviewType: reviewTypeRef.current.__value,
                startDate: dateRangeRef.current.__startValue,
                endDate: dateRangeRef.current.__endValue,
            }
            setQueryCondition(queryCondition);
            const queryStr = StringUtil.objectToQueryString(queryCondition);
            window.history.pushState('', '', '/data/export?' + queryStr);
        }
    }

    const onSearch = async () => {
        const errMsg = Validator.validateQueryCondition(queryCondition);
        if (!StringUtil.isNullOrEmpty(errMsg)) {
            alert(errMsg);
            return;
        }
        try {
            setLastEvaluatedKey(undefined);
            setLoading(true);
            const response = await EEPApi.listReview(ApiUtil.createListReviewRequest(initCondition, undefined));
            setReviewList(response.data.list);
            setLastEvaluatedKey(response.data.lastEvaluatedKey);
        } catch (error: any) {
            console.error("Error while searching review list:", error);
            console.log(error.message);
            console.log(error.response?.data?.message);
            if (error.response?.data?.message != undefined) {
                alert(error.response?.data?.message);
            } else {
                alert("Error while searching all reviews," + error.message + " Please try again later!");
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div>
            <div className='default-margin'>
                <div id='query-box' className='search-box'>
                    <div className='inline-display default-margin'>
                        <KatDropdown placeholder={'--Select Type--'} options={TRANSPORTER_TYPE_OPTION}
                                     label={'Reference SubType'} value={queryCondition.referenceSubType}
                                     ref={referenceSubType}
                                     className='query-input' onChange={conditionChange} data-testid='reference-sub-type'></KatDropdown>
                        <KatDropdown placeholder={'--Select Type--'} options={CASE_TYPE_OPTION}
                                     label={'Case Type'} value={queryCondition.caseType} ref={caseTypeRef} data-testid='case-type'
                                     className='query-input' onChange={conditionChange}></KatDropdown>
                        <KatDropdown placeholder={'--Select Type--'} options={caseSubTypeOptions}
                                     label={'Case SubType'} value={queryCondition.caseSubType} ref={caseSubTypeRef} data-testid='case-sub-type'
                                     className='query-input' onChange={conditionChange}></KatDropdown>
                        <KatDropdown placeholder={'--Select Country--'} options={countryOption} label={'Country'}
                                     value={queryCondition.country} ref={countryRef} data-testid='country'
                                     className='query-input' onChange={conditionChange}></KatDropdown>
                        <KatDropdown placeholder={'--Select Status--'} options={STATUS_OPTION} label={'Review Status'}
                                     value={queryCondition.reviewStatus} ref={reviewStatusRef} data-testid='review-status'
                                     className='query-input' onChange={conditionChange}></KatDropdown>
                        <KatDropdown placeholder={'--Select Type--'} options={REVIEW_TYPE_OPTION}
                                     label={'ReviewType'} value={queryCondition.reviewType} ref={reviewTypeRef} data-testid='review-type'
                                     className='query-input' onChange={conditionChange}></KatDropdown>
                        <KatDateRangePicker startLabel={'Start Date (Required)'} startValue={queryCondition.startDate}
                                            endValue={queryCondition.endDate} ref={dateRangeRef}
                                            endLabel={'End Date (Required)'} data-testid='date-picker'
                                            className='date-picker'
                                            onChange={conditionChange}></KatDateRangePicker>
                    </div>
                    <KatButton onClick={onSearch} data-testid='search' disabled={loading}>Search</KatButton>
                    <KatButton className="horizontal-margin" onClick={downloadPreview} data-testid='download-preview' disabled={loading}>Download Preview</KatButton>
                    <KatButton className="horizontal-margin" onClick={downloadAll} data-testid='download-all' disabled={loading}>Download All</KatButton>
                </div>
                <React.Suspense>
                    {loading ? (
                        <div className={'table-center'}>
                            <KatSpinner size="large"/>
                        </div>
                    ) : (
                        <>
                            {reviewList.length > 0 ? (
                                <ReviewPreviewList reviews={reviewList} />
                            ) : (
                                <div className="table-container">No Data Found</div>
                            )}
                        </>
                    )}
                </React.Suspense>
            </div>
        </div>
    );
}

export default ReviewExport;